/**
 * Internal helpers shared by the base primitives (Popover, Listbox, Combobox,
 * SelectMenu, OptionTile, SegmentedControl, Stepper). Not a public API: import the
 * primitives, not these.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type Dir = 'ltr' | 'rtl';

/** Join class names, skipping falsy parts. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Reading direction of an element: the nearest `dir="rtl|ltr"` ancestor, then the
 * document. Read from the DOM, not from i18n, so a locally forced `dir="ltr"` block
 * (a phone number, a chart) is respected.
 */
export function resolveDir(el: Element | null | undefined): Dir {
  const host = el?.closest?.('[dir="rtl"], [dir="ltr"]');
  if (host) return host.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr';
  if (typeof document !== 'undefined') {
    const doc = document.documentElement.getAttribute('dir') || document.body?.getAttribute('dir');
    if (doc === 'rtl') return 'rtl';
  }
  return 'ltr';
}

/** Page direction from i18n, tolerant of test mocks that omit `i18n.dir`. */
export function useDirection(): Dir {
  const { i18n } = useTranslation();
  if (i18n && typeof i18n.dir === 'function') return i18n.dir() === 'rtl' ? 'rtl' : 'ltr';
  return i18n?.language?.startsWith('ar') ? 'rtl' : 'ltr';
}

type Vars = Record<string, string | number>;

function interpolate(template: string, vars?: Vars): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) =>
    vars && vars[name] !== undefined ? String(vars[name]) : '',
  );
}

/**
 * `t` for the `ui` namespace that always returns a string.
 *
 * The shared test setup mocks `t(key, fallback)` as `fallback || key`, so a plain
 * `t(key, { count })` hands back the options OBJECT and React throws when it renders
 * it. Every page that mounts a primitive would inherit that crash in its own tests;
 * falling back to the interpolated English default keeps them rendering.
 */
export function useUiText() {
  const { t } = useTranslation('ui');
  return useCallback(
    (key: string, defaultValue: string, vars?: Vars): string => {
      const out: unknown = t(key, { defaultValue, ...vars });
      if (typeof out === 'string' && out !== key) return out;
      return interpolate(defaultValue, vars);
    },
    [t],
  );
}

/** Stable DOM id for option `index` of the listbox `listboxId`. */
export function getOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

const TABBABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

/** jsdom has no layout engine; skip geometry checks there instead of hiding everything. */
function hasLayout(): boolean {
  return typeof document !== 'undefined' && document.documentElement.getClientRects().length > 0;
}

/** Keyboard-reachable elements inside `root`, in DOM order. */
export function getTabbable(root: HTMLElement): HTMLElement[] {
  const layout = hasLayout();
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((el) => {
    if (el.tabIndex < 0) return false;
    if (el.hasAttribute('disabled')) return false;
    if (el.closest('[hidden], [inert]')) return false;
    if (layout && el.getClientRects().length === 0) return false;
    return true;
  });
}

export function isFocusable(el: HTMLElement): boolean {
  return el.matches(TABBABLE_SELECTOR) && !el.hasAttribute('disabled');
}

/** Focus `el` itself when it can take focus, otherwise its first tabbable descendant. */
export function focusElementOrFirst(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  const target = isFocusable(el) ? el : getTabbable(el)[0];
  if (!target) return false;
  target.focus({ preventScroll: true });
  return true;
}

/** Convert a px number or a `<n>rem` / `<n>px` string to pixels. */
export function toPx(value: number | string | undefined): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return value;
  const n = parseFloat(value);
  if (Number.isNaN(n)) return undefined;
  if (value.trim().endsWith('rem') && typeof document !== 'undefined') {
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return n * (Number.isNaN(root) ? 16 : root);
  }
  return n;
}
